import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DEFAULT_LABEL_LAYOUT, LabelLayout, labelLayoutFrom, labelPrintCss } from './label-layout';

const STYLE_ELEMENT_ID = 'label-print-page-setup';

/**
 * Applies the Company Settings label layout to the label pages.
 *
 * Each label page calls {@link activate} when it opens and {@link deactivate} when it
 * closes. While active, a print stylesheet sized to the configured label sits in the
 * document head, so the browser print dialog lays out one label per page for a Zebra.
 */
@Injectable({
  providedIn: 'root'
})
export class LabelPrintService {

  private readonly _layout = new BehaviorSubject<LabelLayout>({ ...DEFAULT_LABEL_LAYOUT });

  /** The current layout. Starts at the default until the settings have loaded. */
  readonly layout$: Observable<LabelLayout> = this._layout.asObservable();

  private activeCount = 0;

  constructor(private httpClient: HttpClient) { }

  get layout(): LabelLayout {
    return this._layout.value;
  }

  /**
   * Loads the layout and puts the print stylesheet in place. Reloaded on every label
   * page so a change in Company Settings applies without reloading the app.
   */
  activate(): void {
    this.activeCount++;
    this.applyStyle(this.layout);

    this.loadLayout()
    .pipe(take(1))
    .subscribe((layout) => {
      this._layout.next(layout);

      if (this.activeCount > 0) {
        this.applyStyle(layout);
      }
    });
  }

  deactivate(): void {
    this.activeCount = Math.max(0, this.activeCount - 1);

    if (this.activeCount === 0) {
      const el = document.getElementById(STYLE_ELEMENT_ID);
      if (el) {
        el.remove();
      }
    }
  }

  /**
   * `cache=0` because the settings route caches its reads for a day, and a changed
   * label size has to show up the next time someone prints.
   */
  private loadLayout(): Observable<LabelLayout> {
    return this.httpClient.get(environment.api.endpoint + 'environment-settings/?filter=id||eq||1&cache=0')
    .pipe(map((rows: any) => labelLayoutFrom(Array.isArray(rows) ? rows[0] : rows)))
    // No settings, or they could not be read: printing still works at the default size.
    .pipe(catchError(() => of(this.layout)));
  }

  private applyStyle(layout: LabelLayout): void {
    let el = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;

    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ELEMENT_ID;
      document.head.appendChild(el);
    }

    el.textContent = labelPrintCss(layout);
  }
}
